// components/GuideHistoriquePointages.jsx
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import {
  X, HelpCircle, Users, CheckCircle, Fingerprint, AlertTriangle,
  Send, Camera, Filter, ChevronDown, ChevronUp,
  Info, BookOpen, BarChart2, ChevronRight, ClipboardList
} from 'lucide-react';

const COLOR = '#448696';

const getStatsGuide = (modePause, comptaEnabled, photoEnabled) => [
  {
    icon: Users,
    color: '#448696',
    label: 'Employés',
    desc: "Nombre d'employés uniques ayant au moins un pointage enregistré pour la journée sélectionnée. Un employé pointant plusieurs fois dans la journée compte pour un seul employé.",
    exemple: '3 Employés -3 personnes distinctes ont pointé ce jour-là.'
  },
  {
    icon: BarChart2,
    color: '#8b5cf6',
    label: 'Total',
    desc: "Nombre total de lignes de pointage enregistrées, tous employés et tous types confondus. En mode 4 pointages, un employé complet génère 4 lignes.",
    exemple: '12 Total avec 3 employés - moyenne de 4 pointages par employé.'
  },
  {
    icon: Fingerprint,
    color: '#22c55e',
    label: 'Arrivées',
    desc: "Nombre de pointages de type Arrivée enregistrés pour la journée.",
    exemple: '3 Arrivées - 3 employés ont pointé leur entrée.'
  },
  {
    icon: Fingerprint,
    color: '#3b82f6',
    label: 'Départs',
    desc: "Nombre de pointages de type Départ enregistrés pour la journée.",
    exemple: '2 Départs sur 3 arrivées - 1 employé n\'a pas encore pointé son départ.'
  },
  {
    icon: X,
    color: '#ef4444',
    label: 'Absents',
    desc: "Nombre d'employés officiellement marqués comme absents pour cette journée. Une absence est enregistrée manuellement par un administrateur.",
    exemple: '1 Absent — 1 employé a été déclaré absent pour cette journée.'
  },
  {
    icon: AlertTriangle,
    color: '#f97316',
    label: 'Incomplets',
    desc: modePause === 'pointage'
      ? "Nombre d'employés présents dont il manque au moins un des 4 pointages attendus : Arrivée, Sortie Pause, Retour Pause et Départ. Ces cas nécessitent une vérification et une justification."
      : "Nombre d'employés présents dont il manque au moins un des 2 pointages attendus : Arrivée et Départ. Ces cas nécessitent une vérification et une justification.",
    exemple: modePause === 'pointage'
      ? '2 Incomplets - 2 employés ont pointé leur arrivée mais pas leur départ.'
      : '2 Incomplets - 2 employés ont pointé leur arrivée mais pas leur départ.',
    cliquable: true
  },
  ...(comptaEnabled ? [{
    icon: Send,
    color: '#6366f1',
    label: 'Envoyés',
    desc: "Nombre de lignes de pointage transmises au module de comptabilité. Un pointage envoyé est verrouillé.",
    exemple: '8 Envoyés — 8 lignes ont été validées et transmises en comptabilité.'
  }] : []),
  ...(photoEnabled ? [{
    icon: Camera,
    color: '#0ea5e9',
    label: 'Photos',
    desc: "Nombre de pointages accompagnés d'une photo anti-fraude pour certifier la présence physique de l'employé.",
    exemple: '5 Photos - 5 pointages ont une photo de vérification associée.'
  }] : []),
];

const getFiltresGuide = (modePause) => [
  {
    label: 'Tous',
    color: '#448696',
    desc: "Affiche l'ensemble des lignes de pointage sans filtre. Vue par défaut.",
    conseil: 'Utilisez ce filtre pour avoir une vision globale de la journée.'
  },
  {
    label: 'Présents',
    color: '#22c55e',
    desc: "Affiche les lignes de pointage dont le statut est Présent. Inclut tous les types de pointage des employés présents.",
    conseil: 'Utile pour vérifier rapidement qui est venu travailler.'
  },
  {
    label: 'Absents',
    color: '#ef4444',
    desc: "Affiche les lignes des employés officiellement déclarés absents pour cette journée.",
    conseil: 'Utile pour vérifier les absences déclarées et leur statut.'
  },
  {
    label: 'A temps',
    color: '#22c55e',
    desc: "Affiche les lignes dont la ponctualité est calculée comme A temps, dans les horaires prévus et la tolérance configurée.",
    conseil: 'Permet d\'identifier les employés exemplaires en termes de ponctualité.'
  },
  {
    label: 'Retards',
    color: '#f97316',
    desc: "Affiche les lignes avec un statut de ponctualité Retard, Retard grave ou Tolérance. Regroupe tous les niveaux de retard.",
    conseil: 'Vue rapide pour identifier tous les employés en retard, quel que soit le niveau.'
  },
  {
    label: 'Retards graves',
    color: '#ef4444',
    desc: "Affiche uniquement les lignes avec un retard important dépassant largement l'heure prévue et la tolérance configurée.",
    conseil: 'Ces cas doivent être traités en priorité et peuvent nécessiter une justification formelle.'
  },
  {
    label: 'Départs anticipés',
    color: '#eab308',
    desc: "Affiche les lignes dont le départ a été enregistré avant l'heure prévue de fin de journée.",
    conseil: 'Permet de détecter les employés qui partent avant l\'heure autorisée.'
  },
  {
    label: 'Incomplets',
    color: '#f97316',
    desc: modePause === 'pointage'
      ? "Affiche les employés pour lesquels il manque au moins un des 4 pointages attendus. Ces cas nécessitent une vérification et une justification."
      : "Affiche les employés pour lesquels il manque au moins un des 2 pointages attendus. Ces cas nécessitent une vérification et une justification.",
    conseil: 'Filtre de contrôle quotidien - traitez chaque cas incomplet avant la clôture de journée.'
  },
  {
    label: 'Justifiés',
    color: '#22c55e',
    desc: "Affiche les employés incomplets pour lesquels un responsable a enregistré une justification expliquant la situation.",
    conseil: 'Permet de distinguer les cas traités des cas encore en attente de justification.'
  },
];

const AccordionSection = ({ title, colorBar, icon: Icon, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-1 h-6 rounded-full flex-shrink-0" style={{ backgroundColor: colorBar }} />
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: colorBar + '15' }}>
            <Icon size={15} style={{ color: colorBar }} />
          </div>
          <span className="text-sm font-bold text-gray-800">{title}</span>
        </div>
        {isOpen ? <ChevronUp size={15} className="text-gray-400" /> : <ChevronDown size={15} className="text-gray-400" />}
      </button>
      {isOpen && (
        <div className="px-5 pb-5 space-y-2 border-t border-gray-100 pt-4">
          {children}
        </div>
      )}
    </div>
  );
};

const GuideCard = ({ icon: Icon, color, label, desc, exemple, conseil, cliquable }) => (
  <div className="p-4 rounded-xl border border-gray-100 bg-gray-50/30">
    <div className="flex items-center gap-3 mb-2">
      {Icon && (
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: color }}>
          <Icon size={14} className="text-white" />
        </div>
      )}
      <div className="flex items-center gap-2">
        <p className="text-sm font-semibold text-gray-900">{label}</p>
        {cliquable && (
          <span className="px-2 py-0.5 text-[10px] font-semibold rounded bg-orange-100 text-orange-700 uppercase tracking-wide">
            Cliquable
          </span>
        )}
      </div>
    </div>
    <p className={`text-xs text-gray-500 leading-relaxed ${Icon ? 'pl-11' : ''}`}>{desc}</p>
    {exemple && (
      <p className={`text-xs text-gray-400 mt-1.5 ${Icon ? 'pl-11' : ''}`}>
        <span className="font-semibold text-gray-500">Ex :</span> {exemple}
      </p>
    )}
    {conseil && (
      <p className={`text-xs mt-1.5 font-medium ${Icon ? 'pl-11' : ''}`} style={{ color: COLOR }}>
        {conseil}
      </p>
    )}
  </div>
);

const GuideHistoriquePointages = ({ onClose, modePause = 'automatique', comptaEnabled = false, photoEnabled = false }) => {
  const statsGuide = getStatsGuide(modePause, comptaEnabled, photoEnabled);
  const filtresGuide = getFiltresGuide(modePause);

  return ReactDOM.createPortal(
    <>
      <div className="fixed inset-0 bg-black/50" style={{ zIndex: 9998 }} />
      <div
        className="fixed right-0 w-full max-w-2xl bg-white shadow-2xl flex flex-col"
        style={{ zIndex: 9999, animation: 'hpSlideInRight 0.25s ease-out both', top: 0, bottom: 0, height: '100vh' }}
      >
        {/* HEADER */}
        <div className="flex-shrink-0 px-6 py-5 flex items-center justify-between" style={{ backgroundColor: '#448696' }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center">
              <ClipboardList size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-white tracking-wide">Guide de lecture</h2>
              <p className="text-xs text-white/70 mt-0.5">Historique des pointages</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/15 rounded-lg transition-colors text-white">
            <X size={18} />
          </button>
        </div>

        {/* INTRO */}
        <div className="flex-shrink-0 px-6 py-3 border-b border-gray-100 bg-white">
          <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
            <Info size={14} className="text-gray-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-gray-500 leading-relaxed">
              L'historique permet de consulter l'ensemble des pointages jour par jour.
              Ce guide détaille la signification de chaque indicateur et filtre.
              {modePause === 'pointage' && <span className="font-semibold text-gray-700"> Configuration actuelle : 4 pointages par jour.</span>}
              {modePause === 'automatique' && <span className="font-semibold text-gray-700"> Configuration actuelle : 2 pointages par jour.</span>}
            </p>
          </div>
        </div>

        {/* CONTENU */}
        <div className="flex-1 overflow-y-auto modal-scroll p-5 space-y-3" style={{ backgroundColor: '#fdfcf5' }}>

          <AccordionSection title="Les indicateurs statistiques" colorBar="#448696" icon={BarChart2} defaultOpen={true}>
            {statsGuide.map((item) => (
              <GuideCard key={item.label} {...item} />
            ))}
          </AccordionSection>

          <AccordionSection title="Les filtres par statut" colorBar="#b08d1a" icon={Filter} defaultOpen={true}>
            <div className="px-4 py-3 rounded-lg bg-amber-50 border border-amber-100 mb-1">
              <p className="text-xs text-amber-700 leading-relaxed">
                <span className="font-semibold">Important :</span> les filtres s'appliquent sur les lignes de pointage, à l'exception des filtres Incomplets et Justifiés qui s'appliquent sur les employés. Le compteur peut donc être supérieur au nombre d'employés.
              </p>
            </div>
            {filtresGuide.map((item) => (
              <GuideCard key={item.label} color={item.color} label={item.label} desc={item.desc} conseil={item.conseil} />
            ))}
          </AccordionSection>

          <AccordionSection title="Les justifications" colorBar="#f97316" icon={CheckCircle} defaultOpen={false}>
            <GuideCard
              color="#f97316"
              label="Rôle des justifications"
              desc="Lorsqu'un employé présente des pointages incomplets ou des retards graves, le système requiert l'enregistrement d'une justification. Cela garantit une traçabilité complète des anomalies dans l'historique."
              conseil="Une gestion rigoureuse des justifications permet de maintenir un historique exploitable pour la comptabilité et les audits RH."
            />
            <GuideCard
              color="#448696"
              label="Justification par pointage"
              desc="S'applique à un pointage spécifique présentant un retard grave ou un départ très anticipé. Elle documente la raison de l'anomalie sur ce pointage précis."
              exemple="Réunion urgente - arrivée tardive autorisée par la direction."
            />
            <GuideCard
              color="#22c55e"
              label="Justification globale"
              desc="S'applique à un employé dont plusieurs pointages sont manquants dans la journée. Elle documente la raison globale de l'absence de pointages complets."
              exemple="Employé en déplacement externe - pointage impossible sur site."
            />
            <GuideCard
              color="#22c55e"
              label="Statut Justifié"
              desc="Lorsque toutes les justifications requises sont enregistrées, le statut de l'employé passe automatiquement de Incomplet à Justifié. Le compteur Incomplets se décrémente en conséquence."
              conseil="Traitez tous les cas incomplets en fin de journée pour maintenir un historique cohérent."
            />
          </AccordionSection>

          <AccordionSection title="Navigation par pages" colorBar="#448696" icon={ChevronRight} defaultOpen={false}>
            <GuideCard
              color="#448696"
              label="Pagination - 10 employés par page"
              desc="Pour des raisons de performance, l'historique affiche au maximum 10 employés par page. Les boutons numérotés en bas de la liste permettent de naviguer entre les pages."
              exemple="1–10 sur 54 employés - vous consultez les 10 premiers sur 54 au total."
              conseil="Les filtres par statut et la recherche s'appliquent sur l'ensemble des employés, pas uniquement sur la page visible."
            />
          </AccordionSection>

          {/* Note finale */}
          <div className="px-5 py-4 rounded-xl border border-gray-200 bg-white">
            <p className="text-xs font-semibold text-gray-700 mb-1">Bonne pratique</p>
            <p className="text-xs text-gray-500 leading-relaxed">
              En fin de journée, utilisez le filtre <span className="font-semibold text-gray-700">Incomplets</span> pour identifier rapidement les employés dont les pointages sont incomplets. Traitez chaque cas en enregistrant une justification. Une fois tous les cas traités, l'historique du jour est prêt pour la comptabilité.
            </p>
          </div>

        </div>

        {/* FOOTER */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 bg-white flex items-center justify-between">
          <p className="text-xs text-gray-400">
            Mode : <span className="font-semibold text-gray-600">
              {modePause === 'pointage' ? '4 pointages / jour' : '2 pointages / jour'}
            </span>
          </p>
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-lg font-semibold text-xs border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all"
          >
            Fermer
          </button>
        </div>
      </div>
    </>,
    document.body
  );
};

export default GuideHistoriquePointages;